import React, { useState, useEffect, useCallback } from 'react';
import { BookOpen, ChevronRight, ChevronLeft, AlertTriangle, Download, Plus, X, FileText, BarChart3, Loader2, Star, Clock, CheckCircle, Lock, Unlock, ArrowRight } from 'lucide-react';
import axios from 'axios';
import { generateParentingPlanPdf } from '../utils/pdfGenerator';
import { useSyncRefresh, notifySyncUpdate } from '../hooks/useSyncRefresh';
import {
  StageJurisdiction, StagePartyInfo, StageSafety, StageCustody,
  StageCommunication, StageMedical, StageEducation, StageSchedule,
  StageHolidays, StageTravel, StageFinancial, StageConduct,
  StageDispute, StageReview
} from '../components/plan-builder';

import API from '../utils/api';

var GRADE_COLORS = { 'A+': '#10B981', 'A': '#10B981', 'A-': '#10B981', 'B+': '#5A4E9E', 'B': '#5A4E9E', 'B-': '#5A4E9E', 'C+': '#EAB308', 'C': '#EAB308', 'C-': '#EAB308', 'D+': '#F97316', 'D': '#F97316', 'D-': '#F97316', 'F': '#EF4444' };

function MyPlans() {
  var [drafts, setDrafts] = useState([]);
  var [loading, setLoading] = useState(true);
  var [selected, setSelected] = useState(null);
  var [detail, setDetail] = useState(null);
  var [grading, setGrading] = useState(null);
  var [usage, setUsage] = useState(null);

  function loadDrafts() {
    setLoading(true);
    axios.get(API + '/plan-drafts', { withCredentials: true })
      .then(function (r) { setDrafts(r.data.drafts || []); })
      .catch(function () {})
      .finally(function () { setLoading(false); });
  }
  function loadUsage() {
    axios.get(API + '/plan-drafts/usage', { withCredentials: true })
      .then(function (r) { setUsage(r.data); })
      .catch(function () {});
  }

  useEffect(function () { loadDrafts(); loadUsage(); }, []);
  useSyncRefresh(useCallback(function() { loadDrafts(); loadUsage(); }, []));

  function viewPlan(id) {
    setSelected(id);
    setDetail(null);
    axios.get(API + '/plan-drafts/' + id, { withCredentials: true })
      .then(function (r) { setDetail(r.data); })
      .catch(function () { alert('Failed to load plan'); });
  }

  function gradePlan(id) {
    setGrading(id);
    axios.post(API + '/plan-drafts/' + id + '/grade', {}, { withCredentials: true })
      .then(function (r) {
        loadDrafts();
        if (detail && detail.id === id) {
          setDetail(Object.assign({}, detail, { grade: r.data.grade, analysis: r.data.analysis, status: 'graded' }));
        }
      })
      .catch(function (e) { alert(e.response?.data?.detail || 'Grading failed'); })
      .finally(function () { setGrading(null); });
  }

  if (loading) return <div className="card-surface p-8 text-center text-white/40 text-sm">Loading saved plans...</div>;

  if (selected && detail) {
    var a = detail.analysis;
    var gc = GRADE_COLORS[detail.grade] || '#71717A';
    return (
      <div className="space-y-4" data-testid="plan-detail">
        <button onClick={function () { setSelected(null); setDetail(null); }}
          className="text-xs text-white/40 hover:text-white/60 flex items-center gap-1" data-testid="back-to-plans">
          <ChevronLeft className="w-3.5 h-3.5" /> Back to My Plans
        </button>

        <div className="card-surface p-5">
          <div className="flex items-center justify-between mb-3">
            <h3 className="text-base font-bold">{detail.title}</h3>
            <div className="flex items-center gap-3">
              {detail.grade && (
                <div className="flex items-center gap-2">
                  <div className="w-10 h-10 rounded-xl flex items-center justify-center font-black text-lg" style={{ background: gc + '15', color: gc, border: '1px solid ' + gc + '40' }} data-testid="plan-grade-badge">
                    {detail.grade}
                  </div>
                  {detail.overall_score !== undefined && <span className="text-sm text-white/40">{detail.overall_score}/100</span>}
                </div>
              )}
              {grading === detail.id && (
                <span className="text-xs text-white/40 flex items-center gap-1.5"><Loader2 className="w-3.5 h-3.5 animate-spin" /> Grading...</span>
              )}
            </div>
          </div>
          <div className="flex items-center gap-3 text-xs text-white/30">
            <span>Created: {new Date(detail.created_at).toLocaleDateString()}</span>
            {detail.graded_at && <span>Graded: {new Date(detail.graded_at).toLocaleDateString()}</span>}
          </div>
        </div>

        {a && (
          <>
            {/* Scores */}
            <div className="card-surface p-5" data-testid="plan-scores">
              <h4 className="text-sm font-bold mb-4">Score Breakdown</h4>
              <div className="grid grid-cols-2 sm:grid-cols-5 gap-3">
                {Object.entries(a.scores || {}).map(function (entry) {
                  var label = entry[0].replace(/_/g, ' ');
                  var score = entry[1];
                  var c = score >= 80 ? '#10B981' : score >= 60 ? '#EAB308' : '#EF4444';
                  return (
                    <div key={entry[0]} className="text-center">
                      <div className="text-2xl font-black" style={{ color: c }}>{score}</div>
                      <div className="text-xs text-white/40 capitalize mt-1">{label}</div>
                    </div>
                  );
                })}
              </div>
            </div>

            {/* Summary */}
            <div className="card-surface p-5" data-testid="plan-summary">
              <h4 className="text-sm font-bold mb-3">Assessment Summary</h4>
              <p className="text-xs text-white/60 leading-relaxed whitespace-pre-line">{a.summary}</p>
            </div>

            {/* Strengths & Weaknesses */}
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div className="card-surface p-4">
                <h4 className="text-xs font-bold text-[#10B981] mb-2 flex items-center gap-1.5"><CheckCircle className="w-3.5 h-3.5" /> Strengths</h4>
                <ul className="space-y-1.5">
                  {(a.strengths || []).map(function (s, i) { return <li key={i} className="text-xs text-white/50 flex items-start gap-2"><Star className="w-3 h-3 text-[#10B981] flex-shrink-0 mt-0.5" />{s}</li>; })}
                </ul>
              </div>
              <div className="card-surface p-4">
                <h4 className="text-xs font-bold text-[#EF4444] mb-2 flex items-center gap-1.5"><AlertTriangle className="w-3.5 h-3.5" /> Weaknesses</h4>
                <ul className="space-y-1.5">
                  {(a.weaknesses || []).map(function (s, i) { return <li key={i} className="text-xs text-white/50 flex items-start gap-2"><X className="w-3 h-3 text-[#EF4444] flex-shrink-0 mt-0.5" />{s}</li>; })}
                </ul>
              </div>
            </div>

            {/* Recommendations */}
            {a.recommendations && a.recommendations.length > 0 && (
              <div className="card-surface p-4" data-testid="plan-recommendations">
                <h4 className="text-xs font-bold text-[#5A4E9E] mb-2">Recommendations</h4>
                <ul className="space-y-1.5">
                  {a.recommendations.map(function (r, i) { return <li key={i} className="text-xs text-white/50 flex items-start gap-2"><ChevronRight className="w-3 h-3 text-[#5A4E9E] flex-shrink-0 mt-0.5" />{r}</li>; })}
                </ul>
              </div>
            )}

            {/* Missing Elements */}
            {a.missing_elements && a.missing_elements.length > 0 && (
              <div className="card-surface p-4" data-testid="plan-missing">
                <h4 className="text-xs font-bold text-[#F59E0B] mb-2">Missing Elements</h4>
                <ul className="space-y-1.5">
                  {a.missing_elements.map(function (m, i) { return <li key={i} className="text-xs text-white/50 flex items-start gap-2"><Plus className="w-3 h-3 text-[#F59E0B] flex-shrink-0 mt-0.5" />{m}</li>; })}
                </ul>
              </div>
            )}
          </>
        )}

        {!a && (
          <div className="card-surface p-8 text-center">
            <BarChart3 className="w-8 h-8 text-white/20 mx-auto mb-3" />
            <p className="text-sm text-white/40">This plan hasn't been graded yet. Grading happens automatically when you save a plan.</p>
            {grading === detail.id && (
              <span className="text-xs text-white/40 flex items-center justify-center gap-1.5 mt-3"><Loader2 className="w-3.5 h-3.5 animate-spin" /> Grading in progress...</span>
            )}
          </div>
        )}

        <p className="text-xs text-white/20 text-center">Grading is educational only and does not constitute legal evaluation.</p>
      </div>
    );
  }

  return (
    <div className="space-y-4" data-testid="my-plans-list">
      {/* Plan Usage Info */}
      {usage && (
        <div className={usage.locked
          ? 'p-5 rounded-2xl bg-gradient-to-br from-[#2D2560] to-[#5A4E9E] text-white shadow-lg'
          : 'card-surface p-5'} data-testid="plan-usage">
          {usage.locked ? (
            <div className="text-center space-y-3">
              <Lock className="w-8 h-8 text-white mx-auto" />
              <p className="text-sm font-medium text-white">Parenting Plan Builder is not available on the <span className="capitalize font-semibold">{usage.tier}</span> plan.</p>
              <p className="text-xs text-white/80">Upgrade to Basic or higher to create parenting plans.</p>
              <button onClick={function() { window.dispatchEvent(new CustomEvent('navigate-billing')); }}
                className="inline-flex items-center gap-2 px-4 py-2 rounded-lg bg-white text-[#2D2560] text-xs font-semibold hover:bg-white/90 transition-colors"
                data-testid="plan-usage-upgrade-btn">
                Upgrade <ArrowRight className="w-3.5 h-3.5" />
              </button>
            </div>
          ) : (
            <div className="space-y-3">
              <div className="flex items-center justify-between">
                <p className="text-sm font-medium">Plan Usage (Billing Period)</p>
                <span className="text-xs text-white/40 capitalize">{usage.tier} Plan</span>
              </div>
              <div className="w-full bg-white/[0.06] rounded-full h-2.5">
                <div className="h-2.5 rounded-full transition-all" style={{
                  width: Math.min(100, (usage.used / usage.limit) * 100) + '%',
                  background: usage.remaining === 0 ? '#EF4444' : usage.remaining <= 1 ? '#EAB308' : '#10B981'
                }} />
              </div>
              <div className="flex items-center justify-between text-xs">
                <span className="text-white/50">{usage.used} of {usage.limit} plans used this billing period</span>
                <span className={'font-medium ' + (usage.remaining === 0 ? 'text-[#EF4444]' : 'text-[#10B981]')}>{usage.remaining} remaining</span>
              </div>
              {usage.remaining === 0 && usage.extra_plan_cost > 0 && (
                <p className="text-xs text-[#EAB308] text-center">Additional plans available at ${usage.extra_plan_cost.toFixed(2)} each. Your quota resets on your subscription renewal date each month.</p>
              )}
            </div>
          )}
        </div>
      )}

      {drafts.length === 0 ? (
        <div className="card-surface p-8 text-center">
          <FileText className="w-8 h-8 text-white/20 mx-auto mb-3" />
          <p className="text-sm text-white/40">No saved plans yet. Build a plan and save it to see it here.</p>
        </div>
      ) : (
        <div className="space-y-3">
          {drafts.map(function (d) {
            var gc = GRADE_COLORS[d.grade] || '#71717A';
            return (
              <div key={d.id} className="card-surface p-4 flex items-center justify-between gap-4" data-testid={'plan-card-' + d.id}>
                <button onClick={function () { viewPlan(d.id); }} className="flex-1 text-left flex items-center gap-4 min-w-0">
                  {d.grade ? (
                    <div className="w-10 h-10 rounded-xl flex items-center justify-center font-black text-sm flex-shrink-0" style={{ background: gc + '15', color: gc, border: '1px solid ' + gc + '40' }}>
                      {d.grade}
                    </div>
                  ) : (
                    <div className="w-10 h-10 rounded-xl bg-white/[0.04] border border-white/[0.08] flex items-center justify-center flex-shrink-0">
                      <FileText className="w-4 h-4 text-white/20" />
                    </div>
                  )}
                  <div className="min-w-0">
                    <p className="text-sm font-medium truncate">{d.title}</p>
                    <div className="flex items-center gap-2 text-xs text-white/30 mt-0.5 flex-wrap">
                      <Clock className="w-3 h-3" />{new Date(d.updated_at || d.created_at).toLocaleDateString()}
                      <span className={'px-1.5 py-0.5 rounded text-xs font-medium uppercase ' + (d.status === 'graded' ? 'bg-[#10B981]/10 text-[#10B981]' : 'bg-white/5 text-white/25')}>{d.status}</span>
                      {d.reference_number && (
                        <span className="px-1.5 py-0.5 rounded font-mono bg-[#5A4E9E]/15 text-[#5A4E9E] font-bold" title="Reference number for legal record certification">{d.reference_number}</span>
                      )}
                    </div>
                  </div>
                </button>
                <div className="flex items-center gap-2 flex-shrink-0">
                  {grading === d.id && (
                    <span className="text-xs text-white/40 flex items-center gap-1"><Loader2 className="w-3 h-3 animate-spin" /> Grading...</span>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}



const PlanBuilder = () => {
  const [activeTab, setActiveTab] = useState('build');
  const [currentStage, setCurrentStage] = useState(0);
  const [saving, setSaving] = useState(false);
  const [disclaimerAccepted, setDisclaimerAccepted] = useState(false);
  const [planUsage, setPlanUsage] = useState(null);

  useEffect(function () {
    axios.get(API + '/plan-drafts/usage', { withCredentials: true })
      .then(function (r) { setPlanUsage(r.data); })
      .catch(function () {});
  }, [activeTab]);

  // Load saved party info on mount
  useEffect(function () {
    axios.get(API + '/plan-drafts/party-info', { withCredentials: true })
      .then(function (r) {
        if (r.data && r.data.locked) {
          setPlanData(function (prev) { return Object.assign({}, prev, r.data.data, { partyInfoLocked: true }); });
        }
      })
      .catch(function () {});
  }, []);

  function togglePartyLock() {
    if (planData.partyInfoLocked) {
      // Unlock — clear saved info
      axios.delete(API + '/plan-drafts/party-info', { withCredentials: true }).catch(function () {});
      updateData('partyInfoLocked', false);
    } else {
      // Lock — save party info
      var partyFields = {
        courtName: planData.courtName, courtAddress: planData.courtAddress, caseNumber: planData.caseNumber, relatedCases: planData.relatedCases,
        plaintiffName: planData.plaintiffName, plaintiffAddress: planData.plaintiffAddress,
        plaintiffCity: planData.plaintiffCity, plaintiffState: planData.plaintiffState, plaintiffZip: planData.plaintiffZip,
        plaintiffPhone: planData.plaintiffPhone, plaintiffEmail: planData.plaintiffEmail,
        defendantName: planData.defendantName, defendantAddress: planData.defendantAddress,
        defendantCity: planData.defendantCity, defendantState: planData.defendantState, defendantZip: planData.defendantZip,
        defendantPhone: planData.defendantPhone, defendantEmail: planData.defendantEmail,
      };
      axios.post(API + '/plan-drafts/party-info', partyFields, { withCredentials: true }).catch(function () {});
      updateData('partyInfoLocked', true);
    }
  }

  const [planData, setPlanData] = useState({
    // Stage 0: Jurisdiction
    state: '',
    county: '',
    filingContext: '',
    submittingParty: '',
    existingOrder: '',
    
    // Stage 1: Party & Case Info (Plaintiff / Defendant)
    courtName: '',
    courtAddress: '',
    caseNumber: '',
    relatedCases: [],
    partyInfoLocked: false,
    plaintiffName: '',
    plaintiffAddress: '',
    plaintiffCity: '',
    plaintiffState: '',
    plaintiffZip: '',
    plaintiffPhone: '',
    plaintiffEmail: '',
    defendantName: '',
    defendantAddress: '',
    defendantCity: '',
    defendantState: '',
    defendantZip: '',
    defendantPhone: '',
    defendantEmail: '',
    children: [],
    
    // Stage 2: Safety
    protectiveOrder: 'no',
    dvHistory: 'no',
    supervisedVisitation: 'no',
    supervisedVisitationSchedule: '',
    supervisedVisitationDay: '',
    supervisedVisitationTime: '',
    supervisedVisitationDuration: '',
    supervisedVisitationLocation: '',
    supervisedVisitationFacilityName: '',
    supervisedVisitationSupervisor: '',
    
    // Stage 3: Custody
    physicalCustody: '',
    legalCustody: '',
    decisionMaking: [],
    
    // Stage 4: Communication Rules
    preferredComm: '',
    appCostPayer: '',
    commTimeframe: '',
    emergencyComm: '',
    commBoundaries: [],
    thirdParties: [''],
    phoneCallsEnabled: false,
    phoneCallFrequency: '',
    phoneCallDays: [],
    phoneCallTime: '',
    phoneCallRecording: '',
    
    // Stage 5: Health & Emergency
    medicalDecisions: '',
    yearlyCheckupScheduler: '',
    yearlyCheckupAttendance: '',
    unforeseenVisitScheduler: '',
    emergencyDecision: '',
    healthInsurance: '',
    mentalHealthDecisions: '',
    specialNeeds: '',
    
    // Stage 6: Education & Development
    schoolDistrictResidence: '',
    schoolChoice: '',
    extracurriculars: '',
    academicAuthority: '',
    schoolType: '',
    afterSchoolClubs: '',
    activityCostCap: '',
    iep504Plan: '',
    academicTiebreaker: '',
    religiousUpbringing: '',
    
    // Stage 7: Schedule
    schedulePattern: '',
    parentOutOfState: '',
    outOfStateParent: '',
    schoolYearSchedule: '',
    summerSchedule: '',
    exchangeLocation: '',
    exchangeTime: '',
    childInSchool: 'yes',
    schoolOutExchangeTime: '',
    schoolCustodyTransition: '',
    // 4-week grid: weekGrid[week][day] = '' | 'plaintiff' | 'defendant' | 'exchange'
    weekGrid: [
      ['', '', '', '', '', '', ''],
      ['', '', '', '', '', '', ''],
      ['', '', '', '', '', '', ''],
      ['', '', '', '', '', '', ''],
    ],
    // Overnight assignment per cell — independent of the daytime color
    // weekGridOvernight[week][day] = '' | 'plaintiff' | 'defendant'
    weekGridOvernight: [
      ['', '', '', '', '', '', ''],
      ['', '', '', '', '', '', ''],
      ['', '', '', '', '', '', ''],
      ['', '', '', '', '', '', ''],
    ],
    // Exchange times per day of week (index 0=Sun..6=Sat)
    exchangeTimes: ['', '', '', '', '', '', ''],
    // Custom scheduled events (supervised visitation sessions, therapy, other)
    scheduleEvents: [],
    
    // Stage 8: Holidays & Vacation
    holidays: {
      thanksgiving: 'plaintiff-odd_defendant-even',
      christmas_eve: 'plaintiff-even_defendant-odd',
      christmas_day: 'plaintiff-odd_defendant-even',
      new_years: 'plaintiff-even_defendant-odd',
      easter: 'plaintiff-odd_defendant-even',
      july4th: 'plaintiff-even_defendant-odd',
      labor_day: 'plaintiff-odd_defendant-even',
      memorial_day: 'plaintiff-even_defendant-odd',
      st_patricks_day: 'plaintiff-odd_defendant-even',
      spring_break: 'plaintiff-even_defendant-odd',
      fall_break: 'plaintiff-odd_defendant-even',
      mothers_day: { always: 'mother' },
      fathers_day: { always: 'father' },
    },
    customHolidays: [],
    schoolEventAttendance: '',
    schoolEventFirstAttendee: '',
    schoolEventPublicPolicy: '',
    vacationWeeks: '2',
    vacationNotice: '60days',
    vacationDuringSchool: '',
    vacationSchoolTiming: '',
    vacationSchoolConsent: '',
    summerSchedule: '',
    birthdayRule: '',
    
    // Stage 9: Travel & Relocation
    travelNotice: '72h',
    travelItinerary: 'required',
    neighboringStatesExempt: '',
    relocationNotice: '90days',
    relocationDistance: 'out-of-state',
    passportHolding: 'joint',
    internationalTravel: 'both-consent',
    exchangeTravelCost: '',
    exchangeTravelMileageRate: '',
    
    // Stage 10: Financial Overlays
    childSupport: '',
    medicalExpenses: '',
    educationExpenses: '',
    extracurricularExpenses: '',
    clothingPolicy: '',
    taxDependency: '',
    taxEvenYears: 'plaintiff',  // who claims in EVEN-numbered tax years (Alternate Years option)
    taxOddYears: 'defendant',   // who claims in ODD-numbered tax years
    
    // Stage 11: Conduct Boundaries
    introNewPartner: '',
    newPartnerOvernights: '',
    datingProfileChild: '',
    socialMediaPolicy: '',
    disparagement: 'no-disparagement',
    substancePolicy: '',
    alcoholPolicy: '',
    substanceMonitoring: '',
    monitoringFrequency: '',
    monitoringType: '',
    
    // Stage 12: Dispute Resolution
    disputeProcess: '',
    mediationSessions: '',
    mediationCost: '',
    modificationProcess: '',
    reviewMilestones: '',
    reviewMilestonesList: [],
  });

  const stages = [
    { id: 0, title: 'Jurisdiction & Filing Context', desc: 'Legal framing and disclaimers' },
    { id: 1, title: 'Party & Case Information', desc: 'Court details and parties' },
    { id: 2, title: 'Safety Restrictions and Related Cases', desc: 'Protective orders, supervision, and related cases' },
    { id: 3, title: 'Custody Architecture', desc: 'Physical and legal custody' },
    { id: 4, title: 'Communication Rules', desc: 'How parents communicate' },
    { id: 5, title: 'Health & Emergency Framework', desc: 'Medical decisions' },
    { id: 6, title: 'Education & Development', desc: 'School and activities' },
    { id: 7, title: 'Parenting-Time Schedule', desc: 'Custody schedule pattern' },
    { id: 8, title: 'Holidays & Vacation', desc: 'Special day arrangements' },
    { id: 9, title: 'Travel & Relocation', desc: 'Travel rules and notification' },
    { id: 10, title: 'Financial Overlays', desc: 'Expenses and support' },
    { id: 11, title: 'Conduct Boundaries', desc: 'Behavioral expectations' },
    { id: 12, title: 'Dispute Resolution', desc: 'Handling disagreements' },
    { id: 13, title: 'Review & Generate', desc: 'Preview and download plan' },
  ];

  const updateData = (field, value) => {
    setPlanData({ ...planData, [field]: value });
  };

  const nextStage = () => {
    if (currentStage < stages.length - 1) {
      setCurrentStage(currentStage + 1);
    }
  };

  const prevStage = () => {
    if (currentStage > 0) {
      setCurrentStage(currentStage - 1);
    }
  };

  const generatePDF = async () => {
    await generateParentingPlanPdf(planData, savePlan);
  };


  const STAGE_COMPONENTS = [
    StageJurisdiction, StagePartyInfo, StageSafety, StageCustody,
    StageCommunication, StageMedical, StageEducation, StageSchedule,
    StageHolidays, StageTravel, StageFinancial, StageConduct,
    StageDispute, StageReview
  ];

  const renderStage = () => {
    var StageComponent = STAGE_COMPONENTS[currentStage];
    if (!StageComponent) return null;
    return (
      <StageComponent
        planData={planData}
        updateData={updateData}
        setPlanData={setPlanData}
        togglePartyLock={togglePartyLock}
        generatePDF={generatePDF}
        saving={saving}
      />
    );
  };

  const savePlan = async () => {
    var title = planData.plaintiffName && planData.defendantName
      ? planData.plaintiffName + ' vs ' + planData.defendantName
      : planData.jurisdiction
      ? 'Plan — ' + planData.jurisdiction
      : 'Parenting Plan ' + new Date().toLocaleDateString();
    setSaving(true);
    try {
      var res = await axios.post(API + '/plan-drafts', { title: title, form_data: planData }, { withCredentials: true });
      setActiveTab('my-plans');
      setCurrentStage(0);
      setPlanData(function(prev) {
        var locked = prev.partyInfoLocked;
        var fresh = Object.keys(prev).reduce(function(acc, k) {
          var v = prev[k];
          if (Array.isArray(v)) {
            if (k === 'weekGrid' || k === 'weekGridOvernight') {
              acc[k] = [['','','','','','',''],['','','','','','',''],['','','','','','',''],['','','','','','','']];
            } else if (k === 'exchangeTimes') {
              acc[k] = ['','','','','','',''];
            } else if (k === 'thirdParties') {
              acc[k] = [''];
            } else {
              acc[k] = [];
            }
          }
          else if (typeof v === 'object' && v !== null) acc[k] = k === 'holidays' ? Object.assign({}, v) : {};
          else if (typeof v === 'boolean') acc[k] = false;
          else acc[k] = '';
          return acc;
        }, {});
        if (locked) {
          // Preserve locked party info
          ['partyInfoLocked','courtName','courtAddress','caseNumber','relatedCases',
           'plaintiffName','plaintiffAddress','plaintiffCity','plaintiffState','plaintiffZip','plaintiffPhone','plaintiffEmail',
           'defendantName','defendantAddress','defendantCity','defendantState','defendantZip','defendantPhone','defendantEmail'
          ].forEach(function(k) { fresh[k] = prev[k]; });
        }
        return fresh;
      });
      // Auto-grade in background
      axios.post(API + '/plan-drafts/' + res.data.id + '/grade', {}, { withCredentials: true }).catch(function() {});
    } catch (e) {}
    setSaving(false);
  };

  if (!disclaimerAccepted) {
    return (
      <div className="max-w-3xl mx-auto space-y-6">
        <div className="card-surface p-6">
          <div className="flex items-center gap-3 mb-4">
            <BookOpen className="w-6 h-6 text-[#5A4E9E]" />
            <h2 className="text-2xl font-bold">Parenting Plan Builder</h2>
          </div>
        </div>
        <div className="card-surface p-8 space-y-6" data-testid="plan-builder-disclaimer">
          <div className="flex items-start gap-3">
            <AlertTriangle className="w-6 h-6 text-[#EAB308] flex-shrink-0 mt-0.5" />
            <h3 className="text-lg font-bold text-[#EAB308]">Important Legal Disclaimer</h3>
          </div>
          <div className="bg-white/[0.03] border border-white/[0.08] rounded-xl p-6 max-h-80 overflow-y-auto">
            <p className="text-sm text-white/70 leading-relaxed">
              This PDF is provided by Parentum solely as an organizational aid and general informational resource. It is not legal advice, is not a substitute for an attorney. This PDF is not intended to be a legal document. The inclusion of branding, formatting, and logo elements does not comply with the rules of civil procedure in most jurisdictions, and a judge or judicial officer has full discretion to accept, reject, strike, or disregard this document on that basis or for any other procedural, evidentiary, or jurisdictional reason.
            </p>
            <p className="text-sm text-white/70 leading-relaxed mt-4">
              This document does not constitute legal advice and is not a substitute for representation by a licensed attorney. It is not guaranteed to be admissible, accepted, filed, considered, or relied upon by any court, judicial officer, attorney, mediator, custody evaluator, agency, or other decision-maker.
            </p>
            <p className="text-sm text-white/70 leading-relaxed mt-4">
              Courts and other authorities may reject or disregard this document for any reason, including but not limited to evidentiary issues, procedural defects, formatting deficiencies, authentication problems, lack of foundation, hearsay concerns, violations of local rules, timeliness issues, or other legal deficiencies.
            </p>
            <p className="text-sm text-white/70 leading-relaxed mt-4">
              The user alone is solely responsible for determining whether and how this document may be used, submitted, disclosed, or relied upon, and for ensuring full compliance with all applicable laws, rules of evidence, procedural requirements, court orders, and filing standards. The user is solely responsible for making any requests for business records affidavits certifying the authenticity of the regularly kept business record, which can be found in the users profile navigational tab. Parentum assumes no responsibility or liability for the user's submission, use, non-use, rejection, exclusion, or treatment of this PDF in any matter.
            </p>
          </div>
          <button
            onClick={function() { setDisclaimerAccepted(true); }}
            className="w-full bg-gradient-to-r from-[#2D2560] to-[#5A4E9E] text-white hover:opacity-90 font-semibold rounded-lg py-3 flex items-center justify-center gap-2"
            data-testid="accept-disclaimer-btn">
            I Understand and Accept
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="card-surface p-6">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <BookOpen className="w-6 h-6 text-[#5A4E9E]" />
            <h2 className="text-2xl font-bold">Parenting Plan Builder</h2>
          </div>
          <div className="flex gap-1 bg-white/[0.03] rounded-xl p-1 border border-white/[0.06]" data-testid="plan-builder-tabs">
            <button onClick={function () { setActiveTab('build'); }}
              className={'px-4 py-2 rounded-lg text-xs font-semibold transition-all ' + (activeTab === 'build' ? 'bg-gradient-to-r from-[#2D2560] to-[#5A4E9E] text-white shadow-sm' : 'text-[#6B6690] hover:text-[#2D2560]')}
              data-testid="tab-build">
              Build
            </button>
            <button onClick={function () { setActiveTab('my-plans'); }}
              className={'px-4 py-2 rounded-lg text-xs font-semibold transition-all ' + (activeTab === 'my-plans' ? 'bg-gradient-to-r from-[#2D2560] to-[#5A4E9E] text-white shadow-sm' : 'text-[#6B6690] hover:text-[#2D2560]')}
              data-testid="tab-my-plans">
              My Plans
            </button>
          </div>
        </div>
      </div>

      {activeTab === 'my-plans' && <MyPlans />}

      {activeTab === 'build' && planUsage && (planUsage.locked || planUsage.remaining <= 0) && (
        <div className={planUsage.locked
          ? 'p-8 text-center space-y-4 rounded-2xl bg-gradient-to-br from-[#2D2560] to-[#5A4E9E] text-white shadow-lg'
          : 'card-surface p-8 text-center space-y-4'} data-testid="build-locked">
          <Lock className={planUsage.locked ? 'w-10 h-10 text-white mx-auto' : 'w-10 h-10 text-[#EF4444]/50 mx-auto'} />
          {planUsage.paid_trial_lock ? (
            <>
              <p className="text-base font-semibold text-white">Available After Your Trial</p>
              <p className="text-sm text-white/85">The Parenting Plan Builder unlocks once your 7-day free trial converts to a paid subscription. You can build plans as soon as your first charge goes through.</p>
            </>
          ) : planUsage.locked ? (
            <>
              <p className="text-base font-semibold text-white">Plan Builder Unavailable</p>
              <p className="text-sm text-white/85">The Parenting Plan Builder is not available on the <span className="capitalize font-semibold">{planUsage.tier}</span> plan. Upgrade to Basic or higher to start building parenting plans.</p>
              <button onClick={function() { window.dispatchEvent(new CustomEvent('navigate-billing')); }}
                className="inline-flex items-center gap-2 px-5 py-2.5 rounded-lg bg-white text-[#2D2560] text-sm font-semibold hover:bg-white/90 transition-colors"
                data-testid="build-locked-upgrade-btn">
                Upgrade <ArrowRight className="w-4 h-4" />
              </button>
            </>
          ) : (
            <>
              <p className="text-base font-semibold">Monthly Limit Reached</p>
              <p className="text-sm text-white/50">You have used all {planUsage.limit} parenting plans available on your <span className="capitalize text-white/70">{planUsage.tier}</span> plan this billing period. Your quota resets on your subscription renewal date each month.</p>
              {planUsage.extra_plan_cost > 0 && (
                <p className="text-sm text-[#EAB308]">Additional plans are available at ${planUsage.extra_plan_cost.toFixed(2)} each.</p>
              )}
            </>
          )}
        </div>
      )}

      {activeTab === 'build' && (!planUsage || (!planUsage.locked && planUsage.remaining > 0)) && (
      <>

      {/* Progress Bar */}
      <div className="card-surface p-4">
        <div className="flex items-center justify-between mb-2">
          <span className="text-sm font-medium">Stage {currentStage + 1} of {stages.length}</span>
          <span className="text-sm text-white/60">{Math.round(((currentStage + 1) / stages.length) * 100)}%</span>
        </div>
        <div className="w-full bg-white/10 rounded-full h-2">
          <div
            className="bg-gradient-to-r from-[#2D2560] to-[#5A4E9E] h-2 rounded-full transition-all duration-300"
            style={{ width: `${((currentStage + 1) / stages.length) * 100}%` }}
          />
        </div>
      </div>

      {/* Current Stage */}
      <div className="card-surface p-6">
        <h3 className="text-xl font-bold mb-2">{stages[currentStage].title}</h3>
        <p className="text-white/60 mb-6">{stages[currentStage].desc}</p>
        {renderStage()}
      </div>

      {/* Navigation */}
      <div className="flex gap-4">
        <button
          onClick={prevStage}
          disabled={currentStage === 0}
          className="flex-1 bg-white/5 border border-white/10 text-white hover:bg-white/10 rounded-lg py-3 font-semibold disabled:opacity-30 flex items-center justify-center gap-2"
        >
          <ChevronLeft className="w-5 h-5" />
          Previous
        </button>
        <button
          onClick={nextStage}
          disabled={currentStage === stages.length - 1}
          className="flex-1 bg-gradient-to-r from-[#2D2560] to-[#5A4E9E] text-white hover:opacity-90 rounded-lg py-3 font-semibold disabled:opacity-30 flex items-center justify-center gap-2"
        >
          Next
          <ChevronRight className="w-5 h-5" />
        </button>
      </div>

      </>
      )}
    </div>
  );
};

export default PlanBuilder;
